import { Button, Card, Modal, Table, TableColumns, TableColumnWidth, TableData, TablePreferences } from "@veneer/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SimpleMessageModal from "../../components/Modals/SimpleMessageModal";
import { ISimpleMessageDto } from "../../models/dto/application/ISimpleMessageDto";
import { ISurveyAppointmentDto } from "../../models/dto/surveyApi/ISurveyAppointmentDto";
import surveyAppointmentService from "../../services/surveyApi/surveyAppointment.service";
import SolveSurvey from "../../components/Activities/Survey/SolveSurvey";
import { IQuestion } from "../../models/dto/surveyApi/IQuestionDto";
import { format } from "date-fns";
import BackButton from "../../features/path/backButton";
import { BreadCrumbsEnum } from "../../models/enum/BreadCrumbsEnum";
import '../../assets/css/views/activities/survey.css';
import * as htmlEntities from 'html-entities';
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { sanitizeText } from "../../utils/sanitizeText";

export default function SurveyByAppointment() {
    const params = useParams();
    const [loadingData, setLoadingData] = useState(true);
    const [t] = useTranslation("global");
    const [surveys, setSurveys] = useState<TableData[]>([]);
    const [simpleMessage, setSimpleMessage] = useState<ISimpleMessageDto>({ showModal: false, modalMessage: '', modalTitle: '' });
    const [surveyId, setSurveyId] = useState(0);
    const [questionToShow, setQuestionToShow] = useState<IQuestion[]>();
    const [pos, setPos] = useState('');
    const [appointmentActivityId, setAppointmentActivityId] = useState(0);
    const [activityId, setActivityId] = useState(0);
    const [description, setDescription] = useState<JSX.Element>();
    const [openDescription, setOpenDescription] = useState(false);
    const state: any = useSelector((state: RootState) => state.user);
    const DOMPurify = require('dompurify')(window);

    useEffect(() => {
        getSurveyByAppointment();
    }, []);

    const getSurveyByAppointment = () => {
        let id = Number(params.id);
        setAppointmentActivityId(id);
        setActivityId(Number(params.activityId));
        setPos('');
        
        surveyAppointmentService.getSurveyByAppointment(id)
            .then((r: any) => {
                if (anySurvey(r)) {
                    fillData(r)
                } else {
                    setSimpleMessage({ showModal: true, modalMessage: t('header.noSurveys'), modalTitle: t('header.information') })
                }
            }).finally(() => setLoadingData(false));
    }

    const anySurvey = (data: ISurveyAppointmentDto[]): Boolean => {
        return typeof data !== 'undefined' && data.length > 0;
    }

    const columns: TableColumns[] = [
        { id: 'name', label: t("header.name"), },
        { id: 'validity', label: t("header.validity"), },
        { id: 'description', label: t("header.description"), },
        { id: 'surveyDone', label: t("header.surveyDone"), },
        { id: 'make', label: '' }
    ];

    const columnsWidth: TableColumnWidth[] = [
        { columnId: 'name', width: '40%' },
        { columnId: 'validity', width: '10%' },
        { columnId: 'description', width: '20%' },
        { columnId: 'surveyDone', width: '10%' },
        { columnId: 'make', width: '20%' }
    ]

    const tablePreferences: TablePreferences = {
        width: columnsWidth
    }

    const fillData = (data: ISurveyAppointmentDto[]) => {
        const pagedData: TableData[] = [];
        for (let i = 0; i < data.length; i++) {
            const surveyByAppointment: ISurveyAppointmentDto = data[i];
            let tableData: TableData = {};
            tableData = {
                'name': <div style={{ whiteSpace: 'pre-wrap' }}>{surveyByAppointment.surveyName}</div>,
                'validity': surveyByAppointment.endDate ? format(new Date(surveyByAppointment.endDate), 'dd/MM/yyyy') : '31/12/9999',
                'description': surveyByAppointment.surveyDescription !== '' && <div style={{ cursor:'pointer' }}><a onClick={() => getHtml(surveyByAppointment.surveyDescription)}>{t("header.showDescription")}</a></div>,
                'surveyDone': <div>{surveyByAppointment.surveyCount}</div>,
                'make': <div> <Button small onClick={() => handleSurveyButton(surveyByAppointment, JSON.parse(surveyByAppointment.surveyQuestions))} >{t("header.makeSurvey")}</Button></div>
            }
            pagedData.push(tableData);
        }
        setSurveys(pagedData);
    }


    const handleSurveyButton = (survey: ISurveyAppointmentDto, questions: IQuestion[]): void => {
        setSurveyId(survey.surveyId);
        setPos(survey.posCode);
        setQuestionToShow(questions);
    }

    const handlerReturnAnswer = () => {
        setSurveyId(0);
        setLoadingData(true);
        getSurveyByAppointment();
    }

    const getHtml = (surveyDescription: string): any => {
        setSurveyId(0);
        const sanitizedText = sanitizeText(DOMPurify.sanitize(surveyDescription));
        const decodedText = htmlEntities.decode(sanitizedText);
        const html = <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decodedText)}} />
        setDescription(html);
        setOpenDescription(true);
    }


    return (
        <div>
            <div style={{ padding: '20px' }}>
                <BackButton route={BreadCrumbsEnum.Survey} activityId={activityId} appointmentActivityId={appointmentActivityId} />
                <h4>{t('header.surveys')}</h4>
                <Card appearance="outlined"
                    content={
                        <div style={{ padding: '20px' }}>
                            <Table
                                preferences={tablePreferences}
                                loading={loadingData}
                                columns={columns}
                                data={surveys}
                                css={{
                                    height: '60vh'
                                }}
                                columnResize={true}
                            />
                        </div>
                    }
                />
            </div>
            <SolveSurvey surveyId={surveyId} pos={pos} questionToShow={questionToShow} onReturnAnswer={handlerReturnAnswer}></SolveSurvey>
            <SimpleMessageModal simpleMessage={simpleMessage}></SimpleMessageModal>
            <Modal
                align="start"
                id="modal-id"
                show={openDescription}
                onClose={() => setOpenDescription(false)}
                closeButton
            >
                {description}
            </Modal>
        </div>
    );
}


